/**
 * ArtefactViewer Component
 *
 * Displays individual artefacts with type-specific viewers and common actions.
 * Refactored to use specialized content viewers for better maintainability.
 */

import React from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";

import { Artefact } from "@/types/artefacts";

import {
  CodeArtefactViewer,
  DocumentArtefactViewer,
  TableArtefactViewer,
  HTMLArtefactViewer,
  GraphArtefactViewer,
  FileArtefactViewer,
} from "./ArtefactViewers";
import { ArtefactHeader } from "./ArtefactComponents";

interface ArtefactViewerProps {
  artefact: Artefact;
  /**
   * Passed through to the header so a short card gets the compact band. The
   * viewer does not measure itself, because the list already owns the shared
   * card height and measuring here would mean a second source of truth.
   */
  isCompact?: boolean;
}

const getContentViewer = (type: string) => {
  switch (type) {
    case "code":
      return CodeArtefactViewer;
    case "document":
      return DocumentArtefactViewer;
    case "table":
      return TableArtefactViewer;
    case "html":
      return HTMLArtefactViewer;
    case "graph":
      return GraphArtefactViewer;
    case "file":
      return FileArtefactViewer;
    default:
      return null;
  }
};

export const ArtefactViewer: React.FC<ArtefactViewerProps> = ({ artefact, isCompact = false }) => {
  const { t } = useTranslation();
  const ContentViewer = getContentViewer(artefact.type);

  const renderArtefactContent = () => {
    if (ContentViewer) {
      return <ContentViewer artefact={artefact} />;
    }

    // Fallback for unsupported types
    return (
      <div>
        <div className="text-center text-default-500 py-8 border border-dashed border-default-200 rounded-lg">
          <Icon className="mx-auto mb-2" height={48} icon="solar:code-square-bold" width={48} />
          <p>{t("Unsupported artefact type: {{type}}", { type: artefact.type })}</p>
          <p className="text-xs mt-2">{t("Advanced viewers coming soon")}</p>
        </div>
      </div>
    );
  };

  return (
    /*
      A bounded flex column, not a single scrolling box. The header band holding
      the copy and download controls is a *sibling* of the scroller rather than a
      `sticky` child of it, so the controls cannot be scrolled away and no content
      can pass beneath them. A scroll container clips its own content, which makes
      the band unreachable from inside the scroller without any z-index, and
      without matching the card surface colour (`bg-default-50 dark:bg-default-100`,
      declared over in ArtefactsList). `sticky` would have had to win that paint
      order argument against the graph viewers, whose overlays already sit at
      z-10, z-20 and z-30 in the root stacking context, and it would have pinned
      vertically only, so any horizontal overflow would drag the controls off to
      the right.

      `min-h-0` on the scroller is load-bearing. Without it the flex item keeps its
      content height, the column grows past `h-full`, and the whole card scrolls
      again with the header inside it.
    */
    <div className="h-full flex flex-col overflow-hidden">
      <div
        className="shrink-0 border-b border-divider px-2 pt-2"
        data-testid="artefact-viewer-header"
      >
        <ArtefactHeader artefact={artefact} isCompact={isCompact} />
      </div>

      {/*
        The tab stop replaces an accident of the old layout. The action buttons
        used to sit inside this scroller, so a keyboard user could tab to one and
        then arrow-scroll the content. They are outside it now, and a code or
        document artefact holds nothing focusable, so without an explicit tab stop
        the region would be unreachable by keyboard (WCAG 2.1.1).

        `overscroll-contain` stops a wheel that reaches the end of an artefact from
        chaining into the artefact list behind it and dragging the whole panel.

        The name carries the artefact title because `selectedArtefactIds` in
        ArtefactsList is an array whose toggle appends rather than replaces, so
        several artefacts can be expanded at once. A fixed name would put two or
        more identically announced `region` landmarks in the document, leaving a
        screen reader user navigating by region no way to tell them apart. The
        bare name is kept as the fallback for an artefact saved without a title.
      */}
      <div
        aria-label={
          artefact.title
            ? t("Artefact content for {{title}}", { title: artefact.title })
            : t("Artefact content")
        }
        className="flex-1 min-h-0 overflow-auto overscroll-contain px-2 py-3 focus-visible:outline-2 focus-visible:outline-offset-2"
        data-testid="artefact-viewer-content"
        role="region"
        tabIndex={0}
      >
        {renderArtefactContent()}
      </div>
    </div>
  );
};
